import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fileToCompressedDataUri } from "@/lib/imageUpload";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Reusable image picker with on-device compression.
 *
 * Props:
 *  - value: data-URI / URL string (or null)
 *  - onChange: (newValue|null) => void
 *  - shape: "square" | "wide"  — visual aspect of the preview area
 *  - maxDim: client-side downscale dimension (defaults to 800)
 *  - testIdPrefix: for add/remove buttons
 *  - label: helper text shown when empty
 */
export default function ImageUploadField({
    value,
    onChange,
    shape = "square",
    maxDim = 800,
    testIdPrefix = "image",
    label,
    className = "",
}) {
    const inputRef = useRef(null);
    const { t } = useI18n();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const open = () => inputRef.current?.click();

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";  // allow re-picking the same file
        if (!file) return;
        setError(""); setBusy(true);
        try {
            const dataUri = await fileToCompressedDataUri(file, { maxDim });
            // Approximate decoded size: chars * 0.75 bytes
            if ((dataUri.length * 0.75) > 1_500_000) {
                throw new Error(t("common.imageTooLarge"));
            }
            onChange?.(dataUri);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = () => onChange?.(null);

    const aspect = shape === "wide" ? "aspect-[16/9]" : "aspect-square";
    const pxSize = shape === "wide" ? "max-w-md w-full" : "h-32 w-32";

    return (
        <div className={`space-y-2 ${className}`}>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPick}
                data-testid={`${testIdPrefix}-input`}
            />
            {value ? (
                <div className="relative inline-block">
                    <img
                        src={value}
                        alt="preview"
                        className={`${pxSize} ${aspect} object-cover rounded-2xl border border-border bg-muted`}
                        data-testid={`${testIdPrefix}-preview`}
                    />
                    <button
                        type="button"
                        onClick={remove}
                        className="absolute -top-2 -end-2 h-7 w-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90"
                        data-testid={`${testIdPrefix}-remove`}
                        aria-label="Remove image"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={open}
                    disabled={busy}
                    className={`${pxSize} ${aspect} rounded-2xl border-2 border-dashed border-border hover:border-primary/60 bg-muted/30 flex flex-col items-center justify-center text-muted-foreground hover:text-foreground transition-colors`}
                    data-testid={`${testIdPrefix}-trigger`}
                >
                    {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
                    <span className="mt-1 text-xs font-medium">{busy ? t("common.compressing") : t("common.uploadImage")}</span>
                    {label && <span className="mt-1 text-[11px] opacity-70 px-2 text-center">{label}</span>}
                </button>
            )}
            {value && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={open}
                    disabled={busy}
                    data-testid={`${testIdPrefix}-replace`}
                >
                    {busy ? <Loader2 className="h-3 w-3 me-1 animate-spin" /> : <Upload className="h-3 w-3 me-1" />}
                    {t("common.replaceImage")}
                </Button>
            )}
            {error && <p className="text-xs text-destructive" data-testid={`${testIdPrefix}-error`}>{error}</p>}
        </div>
    );
}
